import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CurrencyPipe } from '@angular/common';
import { AgChartsModule } from 'ag-charts-angular';
import { AgChartOptions } from 'ag-charts-community';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { take } from 'rxjs';
import { AccountingReportsService } from '../accounting-reports.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import { CashFlowPdfGenerator } from './cash-flow-pdf';

@Component({
  selector: 'app-cash-flow-statment',
  standalone: true,
  imports: [FormsModule, CurrencyPipe, AgChartsModule, CollapseModule],
  templateUrl: './cash-flow-statment.component.html',
  styleUrl: './cash-flow-statment.component.css'
})
export class CashFlowStatmentComponent implements OnInit {
  public chartOptions!: AgChartOptions;
  public compositionOptions!: AgChartOptions;
  public period: string = 'This Year';
  public periods: string[] = ['This Month', 'Last Month', 'This Quarter', 'This Year', 'Custom'];
  public isChartCollapsed: boolean = false;
  public isBreakdownCollapsed: boolean = true;
  public startDate: string = '';
  public endDate: string = '';

  // Summary Metrics
  public beginningCash: number = 0;
  public netOperating: number = 0;
  public netInvesting: number = 0;
  public netFinancing: number = 0;
  
  get netChangeInCash(): number {
    return this.netOperating + this.netInvesting + this.netFinancing;
  }
  
  get endingCash(): number {
    return this.beginningCash + this.netChangeInCash;
  }

  // Mock data for table breakdown
  public operatingActivities: any[] = [];
  public investingActivities: any[] = [];
  public financingActivities: any[] = [];
  public chartData: any[] = [];

  constructor(
    private reportsService: AccountingReportsService,
    private sharedService: SharedService
  ) {}

  exportPdf() {
    const generator = new CashFlowPdfGenerator();
    const companyInfo = this.sharedService.company_info;
    const data = {
      netOperating: this.netOperating,
      netInvesting: this.netInvesting,
      netFinancing: this.netFinancing,
      netChange: this.netChangeInCash,
      beginningCash: this.beginningCash,
      endingCash: this.endingCash,
      operating: this.operatingActivities,
      investing: this.investingActivities,
      financing: this.financingActivities
    };
    const doc = generator.generatePdf(data, companyInfo, this.period, this.startDate, this.endDate);
    doc.save(`Cash_Flow_Statement_${this.period.replace(' ', '_')}.pdf`);
  }

  ngOnInit() {
    if (this.sharedService.company_info_loaded) {
      this.setPeriod('This Year');
      return;
    }

    this.sharedService.company_info_retrieved$.pipe(take(1)).subscribe(() => {
      this.setPeriod('This Year');
    });
  }

  loadData() {
    this.reportsService.getCashFlow(this.startDate, this.endDate).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const d = res.data;
          this.beginningCash = d.beginningCash || 0;
          this.netOperating = d.netOperating || 0;
          this.netInvesting = d.netInvesting || 0;
          this.netFinancing = d.netFinancing || 0;
          this.operatingActivities = d.operatingActivities || [];
          this.investingActivities = d.investingActivities || [];
          this.financingActivities = d.financingActivities || [];
          this.chartData = d.chartData || [];
          this.initChart();
        }
      },
      error: (err) => console.error("Error loading Cash Flow data:", err)
    });
  }

  setPeriod(p: string) {
    this.period = p;
    if (p === 'Custom') return;

    const today = new Date();
    let start = new Date();
    let end = new Date();

    if (p === 'This Month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    } else if (p === 'Last Month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (p === 'This Quarter') {
      const quarter = Math.floor(today.getMonth() / 3);
      start = new Date(today.getFullYear(), quarter * 3, 1);
      end = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
    } else if (p === 'This Year') {
      start = new Date(today.getFullYear(), 0, 1);
      end = new Date(today.getFullYear(), 11, 31);
    }

    this.startDate = this.formatDate(start);
    this.endDate = this.formatDate(end);
    this.loadData();
  }

  applyCustomRange() {
    if (this.startDate && this.endDate) {
      this.loadData();
    }
  }

  private formatDate(date: Date): string {
    const d = new Date(date);
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;

    return [year, month, day].join('-');
  }

  private initChart() {
    // 1. Cash Trend (Existing)
    this.chartOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Cash Balance Trend', color: 'white', fontSize: 13 },
      data: this.chartData,
      series: [
        {
          type: 'bar',
          xKey: 'month',
          yKey: 'cash',
          yName: 'Cash on Hand',
          fill: '#4caf50',
          cornerRadius: 4,
          label: {
            enabled: true,
            color: 'white',
            fontWeight: 500,
            formatter: (params) => '$' + (params.value / 1000).toFixed(0) + 'k'
          }
        }
      ],
      axes: [
        {
          type: 'category',
          position: 'bottom',
          label: { color: 'rgba(255, 255, 255, 0.7)' }
        },
        {
          type: 'number',
          position: 'left',
          label: { 
            color: 'rgba(255, 255, 255, 0.7)',
            formatter: (params: any) => '$' + (params.value / 1000).toFixed(0) + 'k'
          },
          gridLine: { style: [{ stroke: 'rgba(255, 255, 255, 0.05)' }] }
        }
      ],
      legend: { enabled: false }
    } as any;

    // 2. Composition (Donut)
    this.compositionOptions = {
      theme: 'ag-default-dark',
      background: { fill: 'transparent' },
      title: { text: 'Cash Flow Composition', color: 'white', fontSize: 13 },
      data: [
        { type: 'Operating', amount: Math.abs(this.netOperating) },
        { type: 'Investing', amount: Math.abs(this.netInvesting) },
        { type: 'Financing', amount: Math.abs(this.netFinancing) }
      ],
      series: [
        {
          type: 'donut',
          angleKey: 'amount',
          calloutLabelKey: 'type',
          innerRadiusRatio: 0.7,
          strokeWidth: 0,
          sectorLabelKey: 'amount',
          sectorLabel: {
            color: 'white',
            fontWeight: 500,
            formatter: (params) => '$' + (params.value / 1000).toFixed(0) + 'k',
          },
          fills: ['#00a8ff', '#9c27b0', '#ff9800'],
        },
      ],
      legend: { enabled: false },
    } as any;
  }
}
